import { ObjectSchema } from "@hapi/joi";
import startOfDay from "date-fns/startOfDay";

import Joi from "fe-shared/src/helpers/validation";
import { getNoOffsetDate } from "fe-shared/src/helpers/dates";
import { ErrorMessages } from "fe-shared/src/types/common";

import { PickupRequestFormValues } from "../../../types";

import {
  MAX_COMMENT_LENGTH,
  MAX_PRIORITY_REASON_LENGTH,
  MAX_QUANTITY,
} from "./config";

export const pickupRequestFormErrorMessages: ErrorMessages = {
  collectorId: {
    "number.base": "pickupRequests.validation.collectorRequired",
  },
  haulerId: {
    "number.base": "pickupRequests.validation.haulerRequired",
  },
  carrierId: {
    "number.base": "pickupRequests.validation.carrierRequired",
  },
  materials: {
    "array.min": "pickupRequests.validation.materialsRequired",
  },
  "materials.materialCode": {
    "string.base": "pickupRequests.validation.materialTypeRequired",
  },
  "materials.quantity": {
    "number.base": "pickupRequests.validation.quantityRequired",
    "number.integer": "pickupRequests.validation.quantityInvalid",
    "number.min": "pickupRequests.validation.quantityInvalid",
    "number.max": "pickupRequests.validation.quantityInvalid",
    "number.unsafe": "pickupRequests.validation.quantityInvalid",
  },
  pickupDateFrom: {
    "date.base": "pickupRequests.validation.dateRequired",
    "date.format": "pickupRequests.validation.datePattern",
    "date.min": "pickupRequests.validation.dateInFuture",
  },
  pickupDateTo: {
    "date.format": "pickupRequests.validation.datePattern",
    "date.min": "pickupRequests.validation.dateInFuture",
  },
  priority: {
    "number.base": "pickupRequests.validation.priorityRequired",
  },
};

const materialItemConfig = Joi.object({
  materialCode: Joi.string().required(),
  quantity: Joi.number().integer().min(1).max(MAX_QUANTITY).required(),
});

// getNoOffsetDate is null-returning only for a null argument; this call always has a date.
const todayNoOffset = getNoOffsetDate(startOfDay(new Date())) as Date;

// A saved request keeps whatever pickup date it already has: editing a comment must not be
// blocked because the pickup was scheduled before today. Callers pass that stored date as the
// floor so a NEW value is still held to today.
export const getPickupRequestFormValidationConfig = (
  isCollectorLocked = false,
  earliestPickupDate: Date = todayNoOffset
): ObjectSchema<PickupRequestFormValues> =>
  Joi.object({
    // Locked (edit mode): the collector is fixed and not rendered as an input at all, so
    // requiring it would block a form the field never appears on.
    collectorId: isCollectorLocked
      ? Joi.number().allow(null)
      : Joi.number().required(),
    // A request may be raised with no hauler - the details page renders "Hauler is not selected"
    // for exactly that state, and TargetCompanyId is nullable on the create contract. Choosing
    // the hauler is its own story, so requiring it here would block every create until it ships.
    haulerId: Joi.number().allow(null),
    // Carrier is assigned after create, via POST /pickupRequests/{number}/carrier once the
    // request is Approved or Requested — CreatePickupRequestDto has no carrier field, so this
    // must stay optional or a complete, submittable form would be blocked on it forever.
    carrierId: Joi.number().allow(null),
    materials: Joi.array().items(materialItemConfig).min(1).required(),
    pickupDateFrom: Joi.date().min(earliestPickupDate).required(),
    pickupDateTo: Joi.date().min(earliestPickupDate).allow(null),
    comment: Joi.string().max(MAX_COMMENT_LENGTH).allow("", null),
    priority: Joi.number().required(),
    priorityReason: Joi.string()
      .max(MAX_PRIORITY_REASON_LENGTH)
      .allow("", null),
  });
